/**
 * ThemeEditor Component for UI-Model-1
 *
 * Visual theme configuration for the UI Model:
 * - Button colors (solid, border, text)
 * - Text colors (title, subtitle, body)
 * - Background colors (main, header, navigation)
 *
 * Adapted from existing UI-Model ThemeEditor.
 */

import React, { useState, useCallback } from 'react';
import { Sketch } from '@uiw/react-color';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Button from 'Components/DesignSystem/Button/Button';
import Divider from 'Components/DesignSystem/Divider/Divider';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useUIModelApi } from '../../contexts/UIModelApiContext';
import { useModule } from '../../contexts/ModuleContext';

interface ThemeEditorProps {
  id?: string;
  projectId: string;
}

export interface UIModelTheme {
  solidButtonBackground: string;
  solidButtonLabel: string;
  borderButtonBorder: string;
  borderButtonLabel: string;
  textButtonLabel: string;
  titleColor: string;
  subtitleColor: string;
  bodyTextColor: string;
  mainBackgroundColor: string;
  headerBackgroundColor: string;
  navigationBackgroundColor: string;
}

const DEFAULT_THEME: UIModelTheme = {
  solidButtonBackground: '#0747A6',
  solidButtonLabel: '#ffffff',
  borderButtonBorder: '#0747A6',
  borderButtonLabel: '#0747A6',
  textButtonLabel: '#0747A6',
  titleColor: '#0747A6',
  subtitleColor: '#172B4D',
  bodyTextColor: '#6D788C',
  mainBackgroundColor: '#ffffff',
  headerBackgroundColor: '#ffffff',
  navigationBackgroundColor: '#ffffff',
};

export const ThemeEditor = ({ id, projectId }: ThemeEditorProps) => {
  const baseId = id || 'theme-editor';
  const { state, dispatch } = useUIModelState();
  const { updateTheme } = useUIModelApi();
  const { hasEditPermission } = useModule();

  // Get theme from state or use defaults
  const currentTheme = (state.model?.theme as unknown as UIModelTheme) || DEFAULT_THEME;
  const [theme, setTheme] = useState<UIModelTheme>({ ...DEFAULT_THEME, ...currentTheme });
  const [activeColorPicker, setActiveColorPicker] = useState<keyof UIModelTheme | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleColorChange = useCallback((key: keyof UIModelTheme, value: string) => {
    setTheme((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleSave = async () => {
    if (!hasEditPermission()) {
      toast.error("You don't have permission to update this model");
      return;
    }

    if (!state.model?.id) {
      toast.error('No model loaded');
      return;
    }

    setIsSaving(true);
    try {
      const result = await updateTheme(projectId, state.model.id, theme as unknown as Record<string, unknown>);
      if (result) {
        // Update state
        dispatch({
          type: 'UPDATE_THEME',
          payload: { theme: theme as unknown as Record<string, unknown> },
        });
        toast.success('Theme updated successfully');
      }
    } catch (error) {
      console.error('Failed to save theme:', error);
      toast.error('Failed to save theme');
    } finally {
      setIsSaving(false);
    }
  };

  /* Dark-mode migration (2026-09-10). Two jobs, two treatments:
     — CHROME (headings, picker labels, hex codes, swatch frames, dividers, Save) is
       builder UI and rides the role tokens; it painted grey-900/600/700 + blue-900
       literals that were near-invisible on the dark page.
     — SAMPLES (the buttons and text drawn in the merchant's colours) are the merchant's
       DATA, kept inline — but drawn on a STAGE painted with the merchant's own
       `mainBackgroundColor`, the ground those colours will actually ship on. Drawn
       straight onto the dark page, "Subtitle Text" in #172B4D simply vanished.
     Also retires the static duplicate testids (`themeeditor-themeeditor-div-2/3`, once
     per picker) for per-picker `${pickerId}-swatch` / `-label`. */
  /* The stage hugs its sample — a tight pad, not a slab ("why white bg", 2026-09-10): the
     merchant ground is still the only honest place to judge these colours, but it should
     read as a swatch of THEIR background under the sample, not a white card in our dark
     page. The fixed column width lives on a transparent wrapper so the pickers stay aligned. */
  const Stage = ({ children, name, className = '' }: { children: React.ReactNode; name: string; className?: string }) => (
    <div className={`tw-shrink-0 ${className}`}>
      <div
        data-testid={`${baseId}-stage-${name}`}
        className="tw-inline-flex tw-items-center tw-rounded-ctl tw-border tw-border-edge tw-p-2"
        style={{ backgroundColor: theme.mainBackgroundColor }}
      >
        {children}
      </div>
    </div>
  );

  const ColorPicker = ({ colorKey, label, value }: {
    colorKey: keyof UIModelTheme;
    label: string;
    value: string;
  }) => {
    const isActive = activeColorPicker === colorKey;
    const pickerId = `${baseId}-color-picker-${colorKey}`;
    return (
      <div role="button"
        id={pickerId}
        data-testid={pickerId}
        className="tw-flex tw-items-center tw-space-x-3 tw-relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div role="button" data-testid={`${pickerId}-swatch`} aria-label={`Pick colour — ${label}`}
          id={`${pickerId}-swatch`}
          className="tw-w-12 tw-h-12 tw-rounded-ctl tw-cursor-pointer tw-border tw-border-edge-strong"
          style={{ backgroundColor: value }}
          onClick={() => setActiveColorPicker(isActive ? null : colorKey)}
        />
        {isActive && (
          <div id={`${pickerId}-palette`} data-testid={`${pickerId}-palette`} className="tw-absolute tw-top-10 tw-left-0 tw-z-50">
            <Sketch
              color={value}
              onChange={(color) => handleColorChange(colorKey, color.hex)}
            />
          </div>
        )}
        <div role="button" data-testid={`${pickerId}-label`} onClick={() => setActiveColorPicker(null)}>
          <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-mb-1 tw-block">{label}</Typography>
          <Typography variant="body-primary" className="tw-text-ink tw-font-medium tw-font-mono tw-block">{value}</Typography>
        </div>
      </div>
    );
  };

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="title-header" as="h2" className="tw-text-ink tw-mb-6">{children}</Typography>
  );
  const RowLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body-primary" className="tw-w-56 tw-text-ink-secondary">{children}</Typography>
  );

  return (
    <div role="button"
      id={baseId}
      data-testid={baseId}
      className="tw-px-20 tw-pt-6 tw-w-full tw-h-full tw-overflow-auto"
      style={{ scrollbarWidth: 'none' }}
      onClick={() => setActiveColorPicker(null)}
    >
      <div className="tw-w-full">
        {/* Buttons Section */}
        <section id={`${baseId}-buttons-section`} className="tw-mb-8">
          <SectionTitle>Buttons</SectionTitle>

          {/* Solid Button */}
          <div className="tw-flex tw-items-center tw-mb-6 tw-gap-10">
            <Stage name="solid-button" className="tw-w-48">
              <button type="button" data-testid="themeeditor-themeeditor-button-1"
                className="tw-px-6 tw-py-3 tw-rounded tw-font-medium tw-text-sm tw-border-2"
                style={{
                  backgroundColor: theme.solidButtonBackground,
                  color: theme.solidButtonLabel,
                  borderColor: theme.solidButtonBackground,
                }}
              >
                Solid Button
              </button>
            </Stage>
            <div className="tw-flex tw-space-x-6">
              <ColorPicker colorKey="solidButtonBackground" label="Button Color" value={theme.solidButtonBackground} />
              <ColorPicker colorKey="solidButtonLabel" label="Label Color" value={theme.solidButtonLabel} />
            </div>
          </div>

          {/* Border Button */}
          <div className="tw-flex tw-items-center tw-mb-6 tw-gap-10">
            <Stage name="border-button" className="tw-w-48">
              <button type="button" data-testid="themeeditor-themeeditor-button-2"
                className="tw-px-6 tw-py-3 tw-rounded tw-font-medium tw-text-sm tw-border-2 tw-bg-transparent"
                style={{ borderColor: theme.borderButtonBorder, color: theme.borderButtonLabel }}
              >
                Border Button
              </button>
            </Stage>
            <div className="tw-flex tw-space-x-6">
              <ColorPicker colorKey="borderButtonBorder" label="Border Color" value={theme.borderButtonBorder} />
              <ColorPicker colorKey="borderButtonLabel" label="Label Color" value={theme.borderButtonLabel} />
            </div>
          </div>

          {/* Text Button */}
          <div className="tw-flex tw-items-center tw-mb-6 tw-gap-10">
            <Stage name="text-button" className="tw-w-48">
              <button type="button" data-testid="themeeditor-themeeditor-button-3"
                className="tw-px-6 tw-py-3 tw-rounded tw-font-medium tw-text-sm tw-bg-transparent tw-border-2 tw-border-transparent"
                style={{ color: theme.textButtonLabel }}
              >
                Text Button
              </button>
            </Stage>
            <div className="tw-flex tw-space-x-6">
              <ColorPicker colorKey="textButtonLabel" label="Label Color" value={theme.textButtonLabel} />
            </div>
          </div>
        </section>

        <Divider className="tw-my-8" />

        {/* Text Section */}
        <section id={`${baseId}-text-section`} className="tw-mb-8">
          <SectionTitle>Text</SectionTitle>

          <div className="tw-flex tw-items-center tw-mb-6 tw-gap-10">
            <Stage name="title-text" className="tw-w-56">
              <div className="tw-text-lg tw-font-medium" style={{ color: theme.titleColor }}>Title Text</div>
            </Stage>
            <ColorPicker colorKey="titleColor" label="Title Color" value={theme.titleColor} />
          </div>

          <div className="tw-flex tw-items-center tw-mb-6 tw-gap-10">
            <Stage name="subtitle-text" className="tw-w-56">
              <div className="tw-text-base" style={{ color: theme.subtitleColor }}>Subtitle Text</div>
            </Stage>
            <ColorPicker colorKey="subtitleColor" label="Subtitle Color" value={theme.subtitleColor} />
          </div>

          <div className="tw-flex tw-items-center tw-mb-6 tw-gap-10">
            <Stage name="body-text" className="tw-w-56">
              <div className="tw-text-base" style={{ color: theme.bodyTextColor }}>Body Text</div>
            </Stage>
            <ColorPicker colorKey="bodyTextColor" label="Body Text Color" value={theme.bodyTextColor} />
          </div>
        </section>

        <Divider className="tw-my-8" />

        {/* Background Section */}
        <section id={`${baseId}-background-section`} className="tw-mb-8">
          <SectionTitle>Background</SectionTitle>

          <div className="tw-flex tw-items-center tw-mb-6">
            <RowLabel>Main Bg</RowLabel>
            <ColorPicker colorKey="mainBackgroundColor" label="Bg Color" value={theme.mainBackgroundColor} />
          </div>

          <div className="tw-flex tw-items-center tw-mb-6">
            <RowLabel>Header Bg</RowLabel>
            <ColorPicker colorKey="headerBackgroundColor" label="Bg Color" value={theme.headerBackgroundColor} />
          </div>

          <div className="tw-flex tw-items-center tw-mb-6">
            <RowLabel>Navigation Bg</RowLabel>
            <ColorPicker colorKey="navigationBackgroundColor" label="Bg Color" value={theme.navigationBackgroundColor} />
          </div>
        </section>

        {/* Save Button */}
        <Button
          variant="primary"
          id={`${baseId}-save-btn`}
          data-testid={`${baseId}-save-btn`}
          onClick={handleSave}
          disabled={isSaving}
          loading={isSaving}
          className="tw-mt-4 tw-mb-6"
        >
          Save
        </Button>

        <div className="tw-h-48" />
      </div>
    </div>
  );
};

export default ThemeEditor;
